/**
 * 发送态/已读态判定（21v，人工指出「已读未读是不是做漏了」）。
 *
 * 数据链一直是通的——WS 的 ReadReceipt 信令会推进会话行的 `peerLastReadId`
 * （`apply.ts`），`content_list` 的 `LastReadId` 也会种进去——**只是从来没有
 * 渲染过**。`apply.ts` 文件头那句「气泡『已读』展示归 10d+」就是当时记下的欠账。
 *
 * 口径逐字对齐旧版 `_messageStatusForReadId` + `_statusIcon`：
 * - 只有**自己发的**消息才有状态标记（对方的消息不显示）；
 * - `pending`→时钟、`failed`→感叹号、已读→双勾（accent 亮色）、其余→单勾；
 * - **群聊一律按「已发送」**：服务端的已读回执是私聊语义（按会话推进一个
 *   `LastReadId`），群里 20 个人谁读了无从表达，旧版同样直接跳过群聊；
 * - **阅后即焚消息不算已读**，除非另有焚烧标记——旧版 `secret && !hasSecretReadMarker`
 *   那一支。新版 secret 行本就不参与多选/转发，这里同样不给已读标记：
 *   `secret` 行的「已读」会触发焚烧倒计时，那是 14.13 的语义，不该被这里抢先表达。
 */
export type SendStatus = 'pending' | 'failed' | 'sent' | 'read';

export interface ReadStateInput {
  /** 服务端消息 id；乐观行为 0 */
  readonly id: number;
  /** 是否自己发的 */
  readonly outgoing: boolean;
  /** outbox 态；undefined = 已落库的服务端行 */
  readonly sendState?: 'pending' | 'uploading' | 'failed' | undefined;
  /** 阅后即焚行 */
  readonly secret?: boolean | undefined;
}

/**
 * 返回该消息要显示的状态标记；`null` = 不显示（对方的消息 / 群聊 / 焚烧行）。
 * `peerReadId` 为对方已读到的消息 id（会话行的 `peerLastReadId`）。
 */
export function sendStatusOf(
  item: ReadStateInput,
  peerReadId: number,
  isGroup: boolean,
): SendStatus | null {
  if (!item.outgoing) return null;
  if (item.sendState === 'failed') return 'failed';
  if (item.sendState === 'pending' || item.sendState === 'uploading') return 'pending';
  if (isGroup) return null; // 群聊没有「谁读了」的表达（旧版同）
  if (item.secret === true) return 'sent'; // 焚烧行不翻已读，见文件头
  return item.id > 0 && item.id <= peerReadId ? 'read' : 'sent';
}
